import { BrowserDataCachePageResult, BrowserDataCacheSourceContext } from './source-adapter';

export interface BrowserDataCacheBatchOptions {
  limit?: number;
  signal?: AbortSignal;
}

export interface BrowserDataCachePaginationOptions<TItem> {
  signal?: AbortSignal;
  stopWhen?: {
    modifiedBeforeOrEqual?: string | number | Date;
  };
  getModifiedAt?: (item: TItem) => string | number | Date | null | undefined;
}

export interface BrowserDataCachePaginationResult<TItem> {
  items: TItem[];
  pagesRead: number;
  stoppedByModifiedTime: boolean;
  nextCursor?: unknown;
}

export function chunkBrowserDataCacheItems<T>(items: T[], limit = 20): T[][] {
  const size = Math.max(1, Math.floor(limit || 1));
  const chunks: T[][] = [];
  for (let i = 0; i < (items || []).length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export async function runBrowserDataCacheBatchChunks<TItem, TResult>(
  items: TItem[],
  runner: (chunk: TItem[], index: number) => Promise<TResult[]> | TResult[],
  options: BrowserDataCacheBatchOptions = {},
): Promise<TResult[]> {
  const results: TResult[] = [];
  const chunks = chunkBrowserDataCacheItems(items || [], options.limit || 20);
  for (let i = 0; i < chunks.length; i++) {
    throwIfAborted(options.signal);
    results.push(...await runner(chunks[i], i));
  }
  return results;
}

export async function collectBrowserDataCachePages<TItem, TCursor = unknown>(
  listPage: (
    context: BrowserDataCacheSourceContext,
    cursor?: TCursor | null,
  ) => Promise<BrowserDataCachePageResult<TItem, TCursor>>,
  options: BrowserDataCachePaginationOptions<TItem> = {},
): Promise<BrowserDataCachePaginationResult<TItem>> {
  const items: TItem[] = [];
  const stopModifiedAt = normalizeTime(options.stopWhen?.modifiedBeforeOrEqual);
  let cursor: TCursor | null | undefined = null;
  let pagesRead = 0;
  let stoppedByModifiedTime = false;

  while (true) {
    throwIfAborted(options.signal);
    const page = await listPage({ signal: options.signal || new AbortController().signal }, cursor);
    pagesRead++;

    for (const item of page.items || []) {
      throwIfAborted(options.signal);
      if (stopModifiedAt != null && options.getModifiedAt) {
        const modifiedAt = normalizeTime(options.getModifiedAt(item));
        if (modifiedAt != null && modifiedAt <= stopModifiedAt) {
          stoppedByModifiedTime = true;
          return {
            items,
            pagesRead,
            stoppedByModifiedTime,
            nextCursor: page.nextCursor ?? undefined,
          };
        }
      }
      items.push(item);
    }

    cursor = page.nextCursor;
    if (!cursor) {
      return {
        items,
        pagesRead,
        stoppedByModifiedTime,
        nextCursor: undefined,
      };
    }
  }
}

export function normalizeBrowserDataCacheTime(value: string | number | Date | null | undefined): number | null {
  return normalizeTime(value);
}

function normalizeTime(value: string | number | Date | null | undefined): number | null {
  if (value == null || value === '') return null;
  const time = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Operation aborted', 'AbortError');
  }
}
